export const DAYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
] as const;

/** Monday–Sunday labels for weekday pickers (Open Booking, Activities, etc.). */
export const WEEKDAY_LABELS_MON_SUN = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
] as const;

type WeekdayLabel = (typeof WEEKDAY_LABELS_MON_SUN)[number];

const WEEKDAY_LABELS_LOWER = new Map(
  WEEKDAY_LABELS_MON_SUN.map((d) => [d.toLowerCase(), d] as const),
);

/** Legacy / abbreviated tokens (e.g. M, T, W, TH, F) → canonical label. */
const WEEKDAY_TOKEN_ALIASES: Record<string, WeekdayLabel> = {
  m: "Monday",
  mon: "Monday",
  mo: "Monday",
  monday: "Monday",
  t: "Tuesday",
  tu: "Tuesday",
  tue: "Tuesday",
  tues: "Tuesday",
  tuesday: "Tuesday",
  w: "Wednesday",
  wed: "Wednesday",
  weds: "Wednesday",
  wednesday: "Wednesday",
  th: "Thursday",
  thu: "Thursday",
  thur: "Thursday",
  thurs: "Thursday",
  r: "Thursday",
  thursday: "Thursday",
  f: "Friday",
  fri: "Friday",
  friday: "Friday",
  sa: "Saturday",
  sat: "Saturday",
  saturday: "Saturday",
  su: "Sunday",
  sun: "Sunday",
  sunday: "Sunday",
};

const resolveWeekdayToken = (token: string): WeekdayLabel | undefined => {
  const t = token.trim();
  if (!t) {
    return undefined;
  }
  const lower = t.toLowerCase();
  const fromLabel = WEEKDAY_LABELS_LOWER.get(lower);
  if (fromLabel) {
    return fromLabel;
  }
  return WEEKDAY_TOKEN_ALIASES[lower];
};

export const parseDaysOfWeekStored = (raw: string): WeekdayLabel[] => {
  const s = raw.trim();
  if (!s) {
    return [];
  }
  if (/^all\s+days\b/i.test(s) || /all\s+days\s+of\s+the\s+week/i.test(s)) {
    return [...WEEKDAY_LABELS_MON_SUN];
  }
  const out: WeekdayLabel[] = [];
  for (const p of s.split(",")) {
    const canon = resolveWeekdayToken(p);
    if (canon && !out.includes(canon)) {
      out.push(canon);
    }
  }
  return out;
};

export const formatDaysOfWeekPayload = (days: readonly string[]): string => {
  if (days.length === 0) {
    return "";
  }
  const set = new Set(days);
  const allSelected = WEEKDAY_LABELS_MON_SUN.every((d) => set.has(d));
  if (allSelected) {
    return "All Days of the week";
  }
  return [...WEEKDAY_LABELS_MON_SUN].filter((d) => set.has(d)).join(", ");
};
